import { createApi } from '@reduxjs/toolkit/query/react';
import { publicBaseQuery } from '@core/api';

/**
 * Emergency Response API using RTK Query
 *
 * PUBLIC API - No authentication required
 * Provides endpoints for candidates to respond to emergency substitution requests
 */
export const emergencyResponseApi = createApi({
  reducerPath: 'emergencyResponseApi',
  baseQuery: publicBaseQuery,
  tagTypes: ['EmergencyResponse'],
  endpoints: (builder) => ({
    /**
     * Get response details
     * @param {string} responseId - Candidate response UUID
     * @returns Response details including candidate name, organization, already_responded flag, etc.
     */
    getResponseDetails: builder.query({
      query: (responseId) => ({
        url: `/api/v1/public/emergency-response/${responseId}/`,
        // Add cache buster to prevent browser caching
        params: { _: Date.now() },
      }),
      // Don't cache this endpoint (always fetch fresh)
      keepUnusedDataFor: 0,
      providesTags: (result, error, responseId) => [{ type: 'EmergencyResponse', id: responseId }],
    }),

    /**
     * Submit response (yes/no answer with availability preferences)
     * @param {Object} params
     * @param {string} params.responseId - Candidate response UUID
     * @param {string} params.answer - "yes" or "no"
     * @param {Object} params.availability - Availability preferences object (8 boolean fields)
     * @returns Success message
     */
    submitResponse: builder.mutation({
      query: ({ responseId, answer, availability = {} }) => ({
        url: `/api/v1/public/emergency-response/${responseId}/submit/`,
        method: 'POST',
        body: {
          answer,
          availability_preferences: availability,
        },
      }),
      // Invalidate the response details after submission
      invalidatesTags: (result, error, { responseId }) => [
        { type: 'EmergencyResponse', id: responseId },
      ],
    }),
  }),
});

export const { useGetResponseDetailsQuery, useSubmitResponseMutation } = emergencyResponseApi;
